import React, { useEffect, useCallback } from "react";
import styled from "@emotion/styled";
import RevenueContainer from "../../../state/containers/RevenueContainer";
import ProjectionsTable from "./ProjectionsTable";
import ProjectionsChart from "./ProjectionsChart";
import {
    ContractType,
    CONTRACT_TYPES,
    ContractStatus,
    OrganizationConfig,
} from "../../../models/revenue/BillingConfig";
import {
    color_text_default,
    color_text_light,
    color_colors_ocean,
    color_colors_growth,
    color_defaults_white,
    color_shades_light,
    color_shades_lightest,
    color_shades_darker,
    dark_red,
} from "../../../constants/colors";

interface ProjectionsTabProps {
    revenueContainer: RevenueContainer;
}

const ProjectionsTab: React.FC<ProjectionsTabProps> = ({
    revenueContainer,
}) => {
    const orgConfigs = revenueContainer.state.revenueConfig.organizations;
    const projections = revenueContainer.state.revenueConfig.projections;
    const projectedRevenue = revenueContainer.state.projectedRevenueByOrg;

    // Initialize default projections once on mount (or when org count changes)
    // using a single batched setState call to avoid cascading re-renders.
    useEffect(() => {
        if (orgConfigs.length === 0) return;
        const currentProjections = revenueContainer.state.revenueConfig.projections;
        const orgIdsNeedingInit: string[] = [];
        for (const orgConfig of orgConfigs) {
            const orgProjection = currentProjections?.organizationProjections.find(
                (op) => op.organizationId === orgConfig.organizationId
            );
            if (!orgProjection || orgProjection.monthlyProjections.length === 0) {
                orgIdsNeedingInit.push(orgConfig.organizationId);
            }
        }
        // Single batched call — handles both init and calculate in one setState
        revenueContainer.initializeAllMissingProjections(orgIdsNeedingInit);
    }, [orgConfigs.length]);

    const handleUpdateProjection = useCallback(
        (orgId: string, month: string, locationCount: number) => {
            revenueContainer.updateProjection(orgId, month, locationCount);
        },
        [revenueContainer]
    );

    const handleAddMonth = useCallback(
        (orgId: string) => {
            revenueContainer.addProjectionMonth(orgId);
        },
        [revenueContainer]
    );

    const handleRemoveMonth = useCallback(
        (orgId: string, month: string) => {
            revenueContainer.removeProjectionMonth(orgId, month);
        },
        [revenueContainer]
    );

    const handleInitialize = useCallback(
        (orgId: string) => {
            revenueContainer.initializeDefaultProjections(orgId);
        },
        [revenueContainer]
    );

    const handleClearProjections = useCallback(
        (orgId: string) => {
            revenueContainer.clearProjections(orgId);
        },
        [revenueContainer]
    );

    if (orgConfigs.length === 0) {
        return (
            <EmptyState>
                <EmptyTitle>No organizations configured</EmptyTitle>
                <EmptyDescription>
                    Go to the Revenue tab and click "Settings" to add organizations first.
                    Projections are based on your revenue configuration.
                </EmptyDescription>
            </EmptyState>
        );
    }

    const hasRevenueData = revenueContainer.state.revenueByOrg.length > 0;

    // Compute total projected revenue across all orgs for each month
    const allMonths = revenueContainer.getAllProjectionMonths();
    let totalProjected = 0;
    for (const orgProj of projectedRevenue) {
        for (const mp of orgProj.monthlyProjections) {
            totalProjected += mp.projectedRevenue;
        }
    }

    const contractTypeGroups = groupOrgsByContractType(orgConfigs);
    const showGroupHeaders = contractTypeGroups.length > 1 ||
        (contractTypeGroups.length === 1 && contractTypeGroups[0].contractType !== null);

    return (
        <Container>
            <Header>
                <HeaderLeft>
                    <h1>Revenue Projections</h1>
                    {totalProjected > 0 && (
                        <TotalBadge>
                            Total Projected: {formatCurrency(totalProjected)}
                            {allMonths.length > 0 && (
                                <MonthRange>
                                    ({allMonths[0]} to {allMonths[allMonths.length - 1]})
                                </MonthRange>
                            )}
                        </TotalBadge>
                    )}
                </HeaderLeft>
            </Header>

            {!hasRevenueData && (
                <InfoPanel>
                    Revenue data has not been loaded yet. Load data from the Revenue tab to get
                    accurate average revenue per location calculations. Without revenue data,
                    projections will show $0.
                </InfoPanel>
            )}

            <ProjectionsChart
                projectedRevenueByOrg={projectedRevenue}
                allMonths={allMonths}
            />

            {contractTypeGroups.map((group) => {
                const groupKey = group.contractType || "__uncategorized__";

                const groupProjectedTotal = group.orgs.reduce((sum, orgConfig) => {
                    const orgProj = projectedRevenue.find(
                        (r) => r.organizationId === orgConfig.organizationId
                    );
                    if (!orgProj) return sum;
                    return sum + orgProj.monthlyProjections.reduce(
                        (s, mp) => s + mp.projectedRevenue, 0
                    );
                }, 0);

                return (
                    <ContractTypeGroup key={groupKey}>
                        {showGroupHeaders && (
                            <ContractTypeHeader>
                                <ContractTypeLabel>
                                    {group.contractType || "Uncategorized"}
                                </ContractTypeLabel>
                                <ContractTypeCount>
                                    {group.orgs.length} org{group.orgs.length !== 1 ? "s" : ""}
                                </ContractTypeCount>
                                {groupProjectedTotal > 0 && (
                                    <ContractTypeRevenue>
                                        {formatCurrency(groupProjectedTotal)}
                                    </ContractTypeRevenue>
                                )}
                            </ContractTypeHeader>
                        )}
                        {group.orgs.map((orgConfig) => {
                            const orgProjected = projectedRevenue.find(
                                (r) => r.organizationId === orgConfig.organizationId
                            );
                            const orgProjectionConfig = projections?.organizationProjections.find(
                                (op) => op.organizationId === orgConfig.organizationId
                            );

                            return (
                                <OrgSection key={orgConfig.organizationId}>
                                    <OrgHeader>
                                        <OrgNameRow>
                                            <OrgName>
                                                {orgConfig.organizationName || orgConfig.organizationId}
                                            </OrgName>
                                            {orgConfig.contractStatus && (
                                                <ContractStatusBadge status={orgConfig.contractStatus}>
                                                    {orgConfig.contractStatus}
                                                </ContractStatusBadge>
                                            )}
                                            {orgProjectionConfig && orgProjectionConfig.monthlyProjections.length > 0 && (
                                                <ClearButton
                                                    onClick={() => handleClearProjections(orgConfig.organizationId)}
                                                    title="Clear all projections for this organization"
                                                >
                                                    Clear
                                                </ClearButton>
                                            )}
                                        </OrgNameRow>
                                        <OrgStats>
                                            <StatItem>
                                                <StatLabel>Current Locations</StatLabel>
                                                <StatValue>{orgProjected?.currentLocationCount ?? 0}</StatValue>
                                            </StatItem>
                                            {orgConfig.totalPossibleLocations != null && (
                                                <StatItem>
                                                    <StatLabel>Total Possible</StatLabel>
                                                    <StatValue>{orgConfig.totalPossibleLocations}</StatValue>
                                                </StatItem>
                                            )}
                                            <StatItem>
                                                <StatLabel>Avg Revenue / Location</StatLabel>
                                                <StatValue>
                                                    {formatCurrency(orgProjected?.averageRevenuePerLocation ?? 0)}
                                                </StatValue>
                                            </StatItem>
                                        </OrgStats>
                                    </OrgHeader>
                                    <ProjectionsTable
                                        orgId={orgConfig.organizationId}
                                        monthlyProjections={orgProjectionConfig?.monthlyProjections || []}
                                        projectedRevenue={orgProjected?.monthlyProjections || []}
                                        averageRevenuePerLocation={orgProjected?.averageRevenuePerLocation ?? 0}
                                        onUpdateProjection={handleUpdateProjection}
                                        onAddMonth={handleAddMonth}
                                        onRemoveMonth={handleRemoveMonth}
                                        onInitialize={handleInitialize}
                                    />
                                </OrgSection>
                            );
                        })}
                    </ContractTypeGroup>
                );
            })}
        </Container>
    );
};

const sortOrgsByName = (orgs: OrganizationConfig[]) =>
    orgs.slice().sort((a, b) => {
        const nameA = (a.organizationName || a.organizationId).toLowerCase();
        const nameB = (b.organizationName || b.organizationId).toLowerCase();
        return nameA.localeCompare(nameB);
    });

const groupOrgsByContractType = (
    orgs: OrganizationConfig[]
): { contractType: ContractType | null; orgs: OrganizationConfig[] }[] => {
    const groups: { contractType: ContractType | null; orgs: OrganizationConfig[] }[] = [];

    for (const ct of CONTRACT_TYPES) {
        const matching = orgs.filter((o) => o.contractType === ct);
        if (matching.length > 0) {
            groups.push({ contractType: ct, orgs: sortOrgsByName(matching) });
        }
    }

    const uncategorized = orgs.filter((o) => !o.contractType);
    if (uncategorized.length > 0) {
        groups.push({ contractType: null, orgs: sortOrgsByName(uncategorized) });
    }

    return groups;
};

const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value);
};

const Container = styled.div``;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    h1 {
        margin: 0;
        color: ${color_text_default};
        font-size: 24px;
    }
`;

const HeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`;

const TotalBadge = styled.div`
    font-size: 16px;
    font-weight: 700;
    color: ${color_colors_growth};
    background: rgba(88, 189, 153, 0.1);
    padding: 6px 16px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const MonthRange = styled.span`
    font-size: 12px;
    font-weight: 400;
    color: ${color_text_light};
`;

const InfoPanel = styled.div`
    background: rgba(30, 126, 185, 0.08);
    border: 1px solid ${color_colors_ocean};
    border-radius: 6px;
    padding: 12px 16px;
    margin-bottom: 20px;
    font-size: 13px;
    color: ${color_colors_ocean};
`;

const OrgSection = styled.div`
    background: ${color_shades_lightest};
    border: 1px solid ${color_shades_light};
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 16px;
`;

const OrgHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
`;

const OrgName = styled.h2`
    margin: 0;
    font-size: 18px;
    color: ${color_text_default};
`;

const OrgNameRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const ClearButton = styled.button`
    background: none;
    border: 1px solid ${dark_red};
    color: ${dark_red};
    font-size: 12px;
    padding: 4px 12px;
    border-radius: 4px;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;

    &:hover {
        background: ${dark_red};
        color: ${color_defaults_white};
    }
`;

const ContractStatusBadge = styled.span<{ status: ContractStatus }>`
    font-size: 11px;
    font-weight: 600;
    padding: 3px 8px;
    border-radius: 3px;
    color: ${({ status }) => (status === "Won" ? color_colors_growth : color_colors_ocean)};
    background: ${({ status }) =>
        status === "Won" ? "rgba(88, 189, 153, 0.1)" : "rgba(30, 126, 185, 0.1)"};
`;

const OrgStats = styled.div`
    display: flex;
    gap: 24px;
`;

const StatItem = styled.div`
    text-align: right;
`;

const StatLabel = styled.div`
    font-size: 11px;
    color: ${color_text_light};
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const StatValue = styled.div`
    font-size: 16px;
    font-weight: 600;
    color: ${color_text_default};
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 60px 24px;
`;

const EmptyTitle = styled.h2`
    color: ${color_text_default};
    margin: 0 0 8px 0;
`;

const EmptyDescription = styled.p`
    color: ${color_text_light};
    font-size: 14px;
    max-width: 400px;
    margin: 0 auto;
`;

const ContractTypeGroup = styled.div`
    margin-bottom: 8px;
`;

const ContractTypeHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 0 8px 0;
    margin-bottom: 4px;
    border-bottom: 2px solid ${color_shades_light};
`;

const ContractTypeLabel = styled.span`
    font-size: 14px;
    font-weight: 700;
    color: ${color_shades_darker};
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const ContractTypeCount = styled.span`
    font-size: 12px;
    color: ${color_text_light};
`;

const ContractTypeRevenue = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${color_colors_growth};
    margin-left: auto;
`;

export default ProjectionsTab;
